import { motion } from "framer-motion";
import { Check, ArrowRight, Quote, Target, Sparkles } from "lucide-react";
import { CONSULTATION_TYPES } from "../data";
import { useBooking } from "../BookingContext";

const PAID = CONSULTATION_TYPES.filter((t) => t.price > 0);

const PERKS = [
  "1-on-1 with a 40-year industry veteran",
  "Safety, leadership & accountability focus",
  "In-person or virtual — your choice",
  "Actionable plan you can use immediately",
];

export default function Coaching() {
  const { openBooking } = useBooking();

  return (
    <section id="coaching" data-testid="coaching-section" className="relative py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[#070E1F] overflow-hidden">
      {/* Signature quote band */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="max-w-4xl"
      >
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-12 bg-[#D4AF37]" />
          <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#D4AF37]">1-on-1 Coaching</span>
        </div>
        <Quote className="text-[#D4AF37]/40" size={44} />
        <blockquote className="font-display text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight leading-[1.05] mt-2">
          “You are either a part of the <span className="gold-text">problem</span> — or a part of the <span className="gold-text">solution</span>.”
        </blockquote>
        <p className="mt-6 text-lg md:text-xl text-[#C9CDD9] flex items-center gap-2 max-w-2xl">
          <Target size={20} className="text-[#D4AF37] shrink-0" />
          Transitioning people from a <span className="text-[#F8F9FA] font-semibold">reactive</span> to a <span className="text-[#F8F9FA] font-semibold">proactive</span> mindset.
        </p>
      </motion.div>

      <div className="mt-14 grid lg:grid-cols-12 gap-10 items-start">
        {/* Left — what you get */}
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
            Private coaching that moves you forward.
          </h3>
          <p className="mt-4 text-[#A3A8B8] leading-relaxed">
            Whether you're an individual leveling up or a leader sharpening your team's safety culture,
            these focused sessions turn insight into action. Prefer to test the waters? Start with a free
            15-minute discovery call.
          </p>
          <div className="mt-6 space-y-3">
            {PERKS.map((p) => (
              <div key={p} className="flex items-start gap-3 text-[#F8F9FA]">
                <Check size={18} className="text-[#D4AF37] shrink-0 mt-0.5" /> {p}
              </div>
            ))}
          </div>
          <button
            onClick={() => openBooking("Individual Training", "free")}
            data-testid="coaching-free-btn"
            className="mt-8 inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 font-bold uppercase tracking-wider text-sm px-6 py-3 transition-colors"
          >
            <Sparkles size={16} /> Free 15-min Discovery Call
          </button>
        </motion.div>

        {/* Right — paid tiers */}
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
          {PAID.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`coaching-tier-${t.id}`}
              className={`relative flex flex-col p-8 bg-[#0C142A] border transition-all hover:-translate-y-1 ${
                i === 1 ? "border-[#D4AF37] shadow-[0_0_40px_rgba(212,175,55,0.15)]" : "border-[#1D2847] hover:border-[#D4AF37]/50"
              }`}
            >
              {i === 1 && (
                <span className="absolute -top-3 left-8 bg-[#D4AF37] text-[#050A15] text-xs font-bold uppercase tracking-wider px-3 py-1">
                  Best Value
                </span>
              )}
              <div className="text-xs uppercase tracking-wider text-[#A3A8B8]">{t.label}</div>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-display text-5xl font-black gold-text">${t.price}</span>
                <span className="text-[#A3A8B8] mb-2 text-sm">/ {t.duration} min</span>
              </div>
              <p className="mt-4 text-[#A3A8B8] flex-1">{t.blurb}</p>
              <button
                onClick={() => openBooking("Individual Training", t.id)}
                data-testid={`coaching-book-${t.id}`}
                className="mt-6 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#050A15] hover:bg-[#F0C84B] font-bold uppercase tracking-wider text-sm px-6 py-3 transition-colors"
              >
                Book {t.duration}-min · ${t.price} <ArrowRight size={16} />
              </button>
              <span className="mt-2 text-center text-xs text-[#A3A8B8]">Secure payment via PayPal / card</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
